<template>
  <div class="wrapper">
    <form
      :class="{
        form: true,
        'form--error': isErrorModalOpen
      }"
      @submit.prevent="handleRecaptcha(RecaptchaActions.PROSPECT_STATUS)"
    >
      <FadeTransition>
        <AdvisorStep
          v-if="isAdvisorModalOpen"
          :is-fetching="isLoading"
          @close="isAdvisorModalOpen = false"
          @submit="updateAdvisorEmail"
        />
        <div v-else>
          <Heading :level="3"> Abra a sua conta </Heading>
          <BaseText size="p6" :color="NebraskaColors.elementSecondary">
            Ao continuar concordo e aceito os
            <Link
              class="form-link"
              href="https://warren.com.br/compliance"
              target="_blank"
            >
              Termos de Uso
            </Link>
            e
            <Link
              class="form-link"
              href="https://s3.amazonaws.com/compliance-warren-com-br-prd/001%20JUR%20001%20-%20Pol%C3%ADtica%20de%20Privacidade%20%28site%29.pdf"
              target="_blank"
            >
              Política de Privacidade
            </Link>
          </BaseText>
          <InputText
            id="name"
            v-model="form.name"
            class="form-input"
            label="Nome completo"
            required
            leading-icon="AA0040"
            :status="getInputStatus('name')"
            :helper-text="getErrorMessage('name')"
            @blur="blurName"
          />
          <InputText
            id="email"
            v-model="form.email"
            class="form-input"
            label="Email de login"
            required
            leading-icon="ED0070"
            :autocomplete="isMobile ? 'on' : 'off'"
            :status="getInputStatus('email')"
            :helper-text="getErrorMessage('email')"
            @blur="blurEmail"
          />
          <AutoCompleteSelector
            v-if="!isMobile"
            ref="autocomplete"
            :email="form.email"
            @update-email="updateEmailByAutocomplete"
          />
          <div class="form-suggestion">
            <BaseText
              v-if="suggestedEmail"
              size="p6"
              :color="NebraskaColors.elementSecondary"
            >
              Você quis dizer
              <Link class="form-suggested" @click="clickSuggestedEmail">{{
                suggestedEmail
              }}</Link>
              ?
            </BaseText>
          </div>
          <InputText
            id="cpf"
            v-model="form.cpf"
            class="form-input"
            label="CPF"
            required
            leading-icon="ED0040"
            :status="getInputStatus('cpf')"
            mask="###.###.###-##"
            :helper-text="getErrorMessage('cpf')"
            @blur="blurCpf"
          />
          <InputText
            id="mobile"
            v-model="form.phone"
            class="form-input"
            label="Celular"
            required
            leading-icon="EC0020"
            mask="(##) #####-####"
            :status="getInputStatus('phone')"
            :helper-text="getErrorMessage('phone')"
            @blur="blurPhone"
          />
          <div v-if="showAdvisorField" class="form-container">
            <div>
              <BaseText size="p4" :color="NebraskaColors.elementPrimary">
                Você já tem assessora ou assessor de investimentos?
              </BaseText>
              <Link
                class="form-container--link"
                @click.prevent="openAdvisorDisclaimer"
              >
                Entenda
              </Link>
            </div>
            <FilterList
              v-model="form.hasAdvisor"
              :options="advisorFormOptions"
            />
            <div
              v-if="showAdvisorDisclaimer"
              class="form-container--disclaimer"
            >
              <BaseText
                size="p5"
                class="form-container--disclaimer--text"
                :color="NebraskaColors.elementNegative"
              >
                Selecione SIM se estiver abrindo conta com o auxílio de uma
                assessora ou assessor de investimentos.
              </BaseText>
              <div class="form-container--disclaimer--icon">
                <ButtonIconContextual
                  label="Fechar disclaimer"
                  size="small"
                  icon="EF0030"
                  @click="closeAdvisorDisclaimer"
                />
              </div>
            </div>
          </div>
          <ButtonPrimary
            id="submit"
            class="form-button"
            label="Continuar"
            :disabled="formFulfilled"
            size="large"
            data-testid="submit-button"
            type="submit"
            :loading="isLoading"
          />
        </div>
      </FadeTransition>
      <ErrorModal
        v-if="isErrorModalOpen && isMobile"
        :is-open="isErrorModalOpen"
        :content="errorModalContent"
        @close="isErrorModalOpen = false"
      />
      <FadeTransition>
        <KeepAlive>
          <ErrorModal
            v-if="isErrorModalOpen && !isMobile"
            :is-open="isErrorModalOpen"
            :content="errorModalContent"
            @close="isErrorModalOpen = false"
          />
        </KeepAlive>
      </FadeTransition>
      <Recaptcha ref="recaptcha" @token="onRecaptchaToken" />
    </form>
  </div>
</template>
<script setup lang="ts">
import { Ref, computed, inject, ref, onBeforeMount, watch } from 'vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  ButtonPrimary,
  Heading,
  InputText,
  Link,
  FilterList,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { debounce } from 'lodash-es';
import { ValidationError } from 'yup';
import { useRoute } from 'vue-router/composables';
import {
  YupError,
  InputStatusType,
  ProspectSignUp,
  SignUpFormSteps,
  Advisor,
  SignUpError,
  IErrorModalContent,
  ECoexistenceStatus
} from '@/modules/onboarding/signup/types';
import { initialFormStepSchema } from '@/modules/onboarding/signup/components/InitialFormStep/validate';
import { emailSuggester } from '@/modules/onboarding/signup/helpers/email-suggester';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { notify } from '@/modules/common/helpers/notification';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import {
  getProspectStatus,
  prospectSignUp
} from '@/modules/onboarding/signup/services/api';
import {
  trackFillCpf,
  trackFillEmail,
  trackFillName,
  trackFillPhone,
  trackSelectAdvisorExistence,
  trackSendForm
} from '@/modules/onboarding/signup/helpers/track-events';
import { ErrorModal } from './components/ErrorModal';
import { AdvisorStep } from './components/AdvisorStep';
import { AutoCompleteSelector } from './components/AutoCompleteSelector';

import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';

const form = ref<ProspectSignUp>({
  name: '',
  email: '',
  cpf: '',
  phone: '',
  hasAdvisor: ''
});

const timer = ref<NodeJS.Timer>();
const errors = ref<YupError[]>([]);
const isLoading = ref<boolean>(false);
const isErrorModalOpen = ref<boolean>(false);
const suggestedEmail = ref<string>('');
const recaptcha = ref<InstanceType<typeof Recaptcha> | null>(null);
const lastRecaptchaAction = ref<RecaptchaActions>();
const recaptchaToken = ref<string>('');
const showAdvisorDisclaimer = ref(false);

const isAdvisorModalOpen = inject('isAdvisorModalOpen') as Ref<boolean>;
const updateIsAdvisorModalOpen = inject('updateIsAdvisorModalOpen') as (
  value: boolean
) => void;
const changeStep = inject('changeStep', (step: SignUpFormSteps) => ({ step }));
const userHaveAdvisor = inject('userHaveAdvisor') as Ref<boolean>;
const advisor = inject('advisor') as Ref<Advisor>;
const advisorLoading = inject('advisorLoading') as Ref<boolean>;
const updateFirstName = inject('updateFirstName', (value: string) => ({
  value
}));
const updateEmail = inject('updateEmail', (value: string) => ({ value }));
const partnerThemeLoading = inject('partnerThemeLoading') as Ref<boolean>;
const isMobile = inject('isMobile') as Ref<boolean>;
const route = useRoute();
const errorModalContent = ref<IErrorModalContent>({
  title: '',
  description: '',
  showButtonToLogin: false
});

const advisorFormOptions = computed(() => {
  return [
    { label: 'Sim', value: 'true', disabled: advisorLoading.value },
    { label: 'Não', value: 'false', disabled: advisorLoading.value }
  ];
});

const showAdvisorField = computed(
  () => !userHaveAdvisor.value && !partnerThemeLoading?.value
);

const formFulfilled = computed(() => {
  const filteredFormValues = Object.entries(form.value)
    .filter(([key]) => key !== 'token')
    .map(([, value]) => value);

  return filteredFormValues.some(value => !value) || errors.value.length > 0;
});

function blurName() {
  validateField({ key: 'name' });
  trackFillName();
}

function blurEmail() {
  validateField({ key: 'email', useDebounce: true });
  trackFillEmail();
}

function blurCpf() {
  validateField({ key: 'cpf' });
  trackFillCpf();
}

function blurPhone() {
  validateField({ key: 'phone' });
  trackFillPhone();
}

function updateEmailByAutocomplete(email: string) {
  form.value.email = email;
  validateField({ key: 'email', useDebounce: true });
}

function updateAdvisorEmail(email: string) {
  form.value.advisorEmail = email;

  checkConditions();
}

function clickSuggestedEmail() {
  form.value.email = suggestedEmail.value;
  suggestedEmail.value = '';
  validateField({ key: 'email', useDebounce: true });
}

function openAdvisorDisclaimer() {
  showAdvisorDisclaimer.value = true;

  timer.value = setTimeout(() => {
    showAdvisorDisclaimer.value = false;
  }, 7000);
}

function closeAdvisorDisclaimer() {
  showAdvisorDisclaimer.value = false;

  if (timer.value) clearTimeout(timer.value);
}

async function validateField({
  key,
  useDebounce = false
}: {
  key: string;
  useDebounce?: boolean;
}) {
  const validationCode = async () => {
    try {
      await initialFormStepSchema.validateAt(key, form.value);
      removeError(key);
    } catch (err) {
      if (err instanceof ValidationError) {
        errors.value.push({
          key,
          value: err.errors[0]
        });
      }
    }
  };

  if (useDebounce) {
    const debouncedValidation = debounce(validationCode, 150);
    debouncedValidation();
  } else {
    await validationCode();
  }
}
function getInputStatus(key: string) {
  return errors.value.find(error => error.key === key)
    ? InputStatusType.Error
    : InputStatusType.Default;
}

function getErrorMessage(key: string) {
  return errors.value.find(error => error.key === key)?.value || '';
}

function removeError(key: string) {
  errors.value = errors.value.filter(error => error.key !== key);
}

async function handleProspectStatus() {
  try {
    const response = await getProspectStatus(
      form.value.email,
      recaptchaToken.value
    );
    if (response.status === ECoexistenceStatus.Prospect) {
      updateEmail(form.value.email);
      updateFirstName(form.value.name.split(' ')[0]);
      changeStep(SignUpFormSteps.Password);
      return true;
    }
  } catch (err) {
    treatBffError(err as SignUpError);
    return true;
  }
}

function handleAdvisorCheckbox() {
  if (form.value.hasAdvisor === 'true' && !form.value.advisorEmail) {
    trackSelectAdvisorExistence();
    updateIsAdvisorModalOpen(true);
    return true;
  }
  return false;
}

async function handleErrors() {
  for (const key of Object.keys(form.value)) {
    await validateField({ key });
  }
  if (errors.value.length > 0) return true;

  return false;
}

function handleEmailSuggestion() {
  const emailSuggestion = emailSuggester(form.value.email);

  const shouldSuggestEmail =
    emailSuggestion.suggestion &&
    !emailSuggestion.isKnownDomain &&
    !suggestedEmail.value;

  suggestedEmail.value = shouldSuggestEmail
    ? emailSuggestion.suggestion ?? ''
    : '';

  return shouldSuggestEmail;
}

async function handleRecaptcha(RecaptchaAction: RecaptchaActions) {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;
  isLoading.value = true;

  try {
    lastRecaptchaAction.value = RecaptchaAction;
    await recaptchaEl.onChallenge(RecaptchaAction);
  } catch {
    isLoading.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
  }
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
  if (
    recaptchaToken.value &&
    lastRecaptchaAction.value === RecaptchaActions.PROSPECT_STATUS
  ) {
    await checkConditions();
  } else if (
    recaptchaToken.value &&
    lastRecaptchaAction.value === RecaptchaActions.SIGN_UP
  ) {
    await submit();
  }
}

async function checkConditions() {
  isLoading.value = true;
  const conditions = [
    handleProspectStatus,
    handleAdvisorCheckbox,
    handleErrors,
    handleEmailSuggestion
  ];

  for (const condition of conditions) {
    const shouldStopSubmit = await condition();
    if (shouldStopSubmit) {
      isLoading.value = false;
      return;
    }
  }

  await handleRecaptcha(RecaptchaActions.SIGN_UP);
}

async function submit() {
  trackSendForm();
  try {
    const filteredForm = { ...form.value };
    delete filteredForm.hasAdvisor;
    filteredForm.cpf = filteredForm.cpf.replace(/\D/g, '');
    filteredForm.phone = filteredForm.phone.replace(/\D/g, '');

    await prospectSignUp(filteredForm, recaptchaToken.value, route.query);
    updateEmail(form.value.email);
    updateFirstName(form.value.name.split(' ')[0]);
    changeStep(SignUpFormSteps.Password);
  } catch (err) {
    isAdvisorModalOpen.value = false;
    treatBffError(err as SignUpError);
  }
  isLoading.value = false;
}

function treatBffError(err: SignUpError) {
  if (err?.error?.locations) {
    errorModalContent.value.title = err.error.locations[0].message.formatted;
    errorModalContent.value.description =
      err.error.locations[1].message.formatted;
    errorModalContent.value.showButtonToLogin =
      err.error.key === 'email_exists';
  } else if (err?.error?.message) {
    errorModalContent.value.title = 'Erro ao criar conta';
    errorModalContent.value.description = err.error.message.formatted;
    errorModalContent.value.showButtonToLogin = false;
  } else {
    errorModalContent.value.title = 'Erro ao criar conta';
    errorModalContent.value.description =
      'Não foi possível realizar a operação, por favor tente novamente.';
    errorModalContent.value.showButtonToLogin = false;
  }
  isErrorModalOpen.value = true;
}

onBeforeMount(() => {
  const route = useRoute();

  Object.keys(form.value).forEach(key => {
    const safeKey = key as keyof ProspectSignUp;
    if (route.query[safeKey]) {
      form.value[safeKey] = route.query[safeKey] as string;
    }
  });

  if (!showAdvisorField.value) form.value.hasAdvisor = 'true';
});

watch(userHaveAdvisor, () => {
  userHaveAdvisor && (form.value.advisorEmail = advisor.value.email);
});
</script>
<style scoped lang="less">
@keyframes fade-in {
  0% {
    opacity: 0;
  }
  80% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.form {
  &-link {
    color: @element-secondary;
    text-decoration: underline;
  }

  &-input {
    width: 100%;
    margin-top: @size-spacing-x600;
  }

  :deep(.text-p-4) {
    z-index: 0;
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &-internal-autofill-previewed,
    &:-webkit-autofill::first-line {
      color: @element-primary;
      -webkit-text-fill-color: @element-primary;
      transition: background-color 5000s ease-in-out 0s;
    }
  }

  &-container {
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: @size-spacing-x500;

    &--link {
      .text-p-6();
      color: @element-secondary;
      margin-top: @size-spacing-x300;
    }

    &--disclaimer {
      position: absolute;
      top: -50px;
      background-color: @element-primary;
      padding: @size-spacing-x500;
      border-radius: @size-radius-x200;
      display: flex;
      justify-content: flex-start;

      &--icon {
        width: 32px;
        margin-left: @size-spacing-x400;
      }

      @media screen and (max-width: 380px) {
        top: -100px;
      }

      @media screen and (min-width: 381px) and (max-width: 768px) {
        top: -75px;
      }
    }
  }

  &-button {
    width: 100%;
    margin-top: @size-spacing-x600;
  }

  &-suggestion {
    margin-top: @size-spacing-x150;
  }

  &-suggested {
    color: #15c;
    cursor: pointer;
    text-decoration: underline;
    margin-top: @size-spacing-x150;
  }

  @media screen and (max-width: 768px) {
    width: 100%;

    &--error {
      display: none;
    }

    &-input {
      margin-top: @size-spacing-x500;
    }

    &-wrapper {
      flex-direction: column;
      gap: 0;
    }
  }
}

.wrapper {
  width: 100%;
}

:deep(.helper-view) {
  max-width: none;
}
</style>
